import { useMutation } from '@tanstack/react-query';
import { resetDepositTransaction, resetStuckDepositTransactions } from '../api/deposit';
import { showSuccessNotification, showErrorNotification } from '../utils/notificationUtils';
import { useTranslation } from 'react-i18next';

/**
 * Hook for resetting a deposit transaction
 * @returns Mutation for resetting a deposit transaction
 */
export const useResetDepositTransaction = () => {
  const { t } = useTranslation();
  
  return useMutation({
    mutationFn: (id: string) => resetDepositTransaction(id),
    onSuccess: (data) => {
      if (data.status) {
        showSuccessNotification(
          t('deposit:resetTransaction.success', 'Transaction Reset'),
          t('deposit:resetTransaction.successMessage', 'Transaction has been reset successfully')
        );
      } else {
        showErrorNotification(
          t('deposit:resetTransaction.error', 'Reset Failed'),
          data.error || t('deposit:resetTransaction.unknownError', 'An unknown error occurred')
        );
      }
    },
    onError: (error: any) => {
      showErrorNotification(
        t('deposit:resetTransaction.error', 'Reset Failed'),
        error?.message || t('deposit:resetTransaction.unknownError', 'An unknown error occurred')
      );
    }
  });
};

/**
 * Hook for resetting stuck deposit transactions
 * @returns Mutation for resetting stuck deposit transactions
 */
export const useResetStuckDepositTransactions = () => {
  const { t } = useTranslation();
  
  return useMutation({
    mutationFn: (options: { olderThanMinutes?: number; statuses?: string[] } = {}) => 
      resetStuckDepositTransactions(options),
    onSuccess: (data) => {
      if (data.status) {
        const count = data.data?.modifiedCount || 0;
        // Use a simpler approach to avoid TypeScript errors
        const successMessage = `Reset ${count} stuck transactions`;
        showSuccessNotification(
          t('deposit:resetStuckTransactions.success', 'Transactions Reset'),
          successMessage
        );
      } else {
        showErrorNotification(
          t('deposit:resetStuckTransactions.error', 'Reset Failed'),
          data.error || t('deposit:resetStuckTransactions.unknownError', 'An unknown error occurred')
        );
      }
    },
    onError: (error: any) => {
      showErrorNotification(
        t('deposit:resetStuckTransactions.error', 'Reset Failed'),
        error?.message || t('deposit:resetStuckTransactions.unknownError', 'An unknown error occurred')
      );
    }
  });
};
